import { getActiveLogs } from "@/lib/consent/datadog";

/**
 * Application logging that works everywhere:
 * - on the server: the console (collected by the hosting platform's logs);
 * - in the browser: Datadog when the visitor accepted analytics, otherwise
 *   the console (errors and warnings only; info only during development).
 *
 * Never pass personal data (emails, names, tokens, request bodies) in
 * messages or context.
 */
interface LogContext {
  [key: string]: unknown;
}

const isBrowser = () => typeof window !== "undefined";

function toConsoleArgs(context?: LogContext): unknown[] {
  return context === undefined ? [] : [context];
}

function describeError(error: unknown): {
  errorMessage: string;
  name?: string;
  stack?: string;
} {
  if (error instanceof Error) {
    return {
      errorMessage: error.message,
      name: error.name,
      stack: error.stack,
    };
  }
  if (typeof error === "string") return { errorMessage: error };

  try {
    return { errorMessage: JSON.stringify(error) ?? String(error) };
  } catch {
    return { errorMessage: String(error) };
  }
}

/** Log an error with optional structured context. */
export function logError(
  message: string,
  error: unknown,
  context?: LogContext,
): void {
  const logs = isBrowser() ? getActiveLogs() : null;

  if (logs) {
    try {
      logs.logger.error(message, {
        ...context,
        ...describeError(error),
      });

      return;
    } catch {
      // Fall through to the console.
    }
  }

  console.error(message, error, ...toConsoleArgs(context));
}

/** Log a warning. */
export function logWarning(message: string, context?: LogContext): void {
  const logs = isBrowser() ? getActiveLogs() : null;

  if (logs) {
    try {
      logs.logger.warn(message, context ?? {});

      return;
    } catch {
      // Fall through to the console.
    }
  }

  console.warn(message, ...toConsoleArgs(context));
}

/** Log an informational message. */
export function logInfo(message: string, context?: LogContext): void {
  if (!isBrowser()) {
    // eslint-disable-next-line no-console
    console.info(message, ...toConsoleArgs(context));

    return;
  }

  const logs = getActiveLogs();

  if (logs) {
    try {
      logs.logger.info(message, context ?? {});

      return;
    } catch {
      // Fall through to the console.
    }
  }

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.info(message, ...toConsoleArgs(context));
  }
}
